import * as React from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";
import { utils, write } from "xlsx";
import LocalizedDataGrid from "../../components/ui/datagrid/LocalizedDataGrid";
import { NoRowOverlay } from "../../components/table/NoRowOverlay";
import { getToday } from "../../components/ui/utils/DateUtils";
import {
  getAnalyticProtocols,
  getAnalyticStudies,
  runAnalyticQuery,
} from "./analytic-service";

const transformationName = true;

function getStudyLabel(study) {
  if (!study) {
    return "";
  }

  return study.sponsorStudyCode || study.cdlStudyCode || String(study.id);
}

function s2ab(value) {
  const buffer = new ArrayBuffer(value.length);
  const view = new Uint8Array(buffer);

  for (let index = 0; index < value.length; index += 1) {
    view[index] = value.charCodeAt(index) & 0xff;
  }

  return buffer;
}

function getExportDateRangeText(from, to) {
  if (from && to) {
    return `${from} -> ${to}`;
  }

  if (from) {
    return `${from} ->`;
  }

  if (to) {
    return `-> ${to}`;
  }

  return "All dates";
}

function downloadExcel(data, filename, options = {}) {
  if (!Array.isArray(data) || data.length === 0) {
    return;
  }

  const headers = Object.keys(data[0]);
  const rows = [
    ["Date Range", options.dateRange || "All dates"],
    ["Total", options.total || ""],
    [],
    headers,
  ];

  data.forEach((row) => {
    rows.push(headers.map((header) => row[header]));
  });

  const worksheet = utils.aoa_to_sheet(rows);
  const headerRowIndex = 3;
  const countColumnIndex = headers.indexOf("Count");

  worksheet["!cols"] = headers.map((header) => {
    const width =
      header === "Material Type"
        ? 34
        : header === "Protocols"
          ? 24
          : header === "Production Date" || header === "QC Date"
            ? 18
            : header === "Production ID" || header === "Material ID"
              ? 15
              : header === "Count"
                ? 12
                : 14;

    return { wch: width };
  });

  if (headers.length > 1) {
    worksheet["!merges"] = [
      { s: { r: 0, c: 1 }, e: { r: 0, c: headers.length - 1 } },
      { s: { r: 1, c: 1 }, e: { r: 1, c: headers.length - 1 } },
    ];
  }

  const applyCellStyle = (rowIndex, columnIndex, style) => {
    const cellRef = utils.encode_cell({ r: rowIndex, c: columnIndex });

    if (worksheet[cellRef]) {
      worksheet[cellRef].s = style;
    }
  };

  const strongStyle = {
    font: { bold: true },
  };
  const strongLargeStyle = {
    font: { bold: true, sz: 14 },
  };

  applyCellStyle(0, 0, strongStyle);
  applyCellStyle(0, 1, strongStyle);
  applyCellStyle(1, 0, strongLargeStyle);
  applyCellStyle(1, 1, strongLargeStyle);

  headers.forEach((_, columnIndex) => {
    applyCellStyle(headerRowIndex, columnIndex, strongStyle);
  });

  if (countColumnIndex >= 0) {
    for (let rowIndex = headerRowIndex + 1; rowIndex < rows.length; rowIndex += 1) {
      applyCellStyle(rowIndex, countColumnIndex, strongStyle);
    }
  }

  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, "Analytics");

  const output = write(workbook, {
    bookType: "xlsx",
    type: "binary",
    cellStyles: true,
  });

  saveAs(
    new Blob([s2ab(output)], { type: "application/octet-stream" }),
    `${filename}.xlsx`,
  );
}

function getDateRangeLabel(from, to) {
  if (from && to) {
    return from === to ? from : `${from}_to_${to}`;
  }

  if (from) {
    return `${from}_from`;
  }

  if (to) {
    return `until_${to}`;
  }

  return "all-dates";
}

function getTransformedMaterialName(materialName) {
  if (!transformationName || !materialName) {
    return materialName || "";
  }

  return String(materialName).replace(/^(\d+)-Kit\b/i, "Kit $1");
}

function sortAnalyticRows(leftRow, rightRow) {
  const materialCompare = String(leftRow.materialName || "").localeCompare(
    String(rightRow.materialName || ""),
    undefined,
    { numeric: true, sensitivity: "base" },
  );

  if (materialCompare !== 0) {
    return materialCompare;
  }

  const protocolCompare = String(leftRow.protocolReference || "").localeCompare(
    String(rightRow.protocolReference || ""),
    undefined,
    { numeric: true, sensitivity: "base" },
  );

  if (protocolCompare !== 0) {
    return protocolCompare;
  }

  return String(leftRow.productionId || "").localeCompare(
    String(rightRow.productionId || ""),
    undefined,
    { numeric: true, sensitivity: "base" },
  );
}

function formatPrice(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? "" : numericValue.toFixed(2);
}

function formatTotalPrice(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return "";
  }

  const [integerPart, decimalPart] = numericValue.toFixed(2).split(".");
  const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  return `${groupedInteger},${decimalPart}`;
}

function buildExportRows(rows, includeProtocol) {
  return rows.map((row) => ({
    "Material Type": row.materialName,
    ...(includeProtocol ? { Protocols: row.protocolReference } : {}),
    "Production Date": row.productionDate,
    "QC Date": row.qcDate,
    Count: row.count === null || row.count === undefined ? "" : String(row.count),
    "Unit Price": formatPrice(row.unitPrice),
    "Sub Price": formatPrice(row.subPrice),
  }));
}

export const ScreenAnalytic = () => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.user);
  const [studies, setStudies] = React.useState([]);
  const [protocols, setProtocols] = React.useState([]);
  const [protocolMode, setProtocolMode] = React.useState("all");
  const [selectedStudyId, setSelectedStudyId] = React.useState("");
  const [selectedProtocolIds, setSelectedProtocolIds] = React.useState([]);
  const [from, setFrom] = React.useState("");
  const [to, setTo] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [detailRows, setDetailRows] = React.useState([]);
  const [exportRows, setExportRows] = React.useState([]);
  const [includeProtocolColumn, setIncludeProtocolColumn] =
    React.useState(false);
  const [loadingStudies, setLoadingStudies] = React.useState(true);
  const [loadingProtocols, setLoadingProtocols] = React.useState(false);
  const [loadingResults, setLoadingResults] = React.useState(false);
  const [error, setError] = React.useState("");
  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  const [resultsOpen, setResultsOpen] = React.useState(false);
  const [detailsOpen, setDetailsOpen] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    const loadStudies = async () => {
      try {
        setLoadingStudies(true);
        const result = await getAnalyticStudies(user);

        if (!isMounted) {
          return;
        }

        const sortedStudies = [...result].sort((leftStudy, rightStudy) =>
          getStudyLabel(leftStudy).localeCompare(getStudyLabel(rightStudy)),
        );

        setStudies(sortedStudies);

        if (sortedStudies.length === 1) {
          setSelectedStudyId(String(sortedStudies[0].id));
        }
      } catch (loadError) {
        if (isMounted) {
          setError(t("analyticsLoadError"));
        }
      } finally {
        if (isMounted) {
          setLoadingStudies(false);
        }
      }
    };

    loadStudies();

    return () => {
      isMounted = false;
    };
  }, [t, user]);

  React.useEffect(() => {
    let isMounted = true;

    const loadProtocols = async () => {
      if (!selectedStudyId) {
        setProtocols([]);
        setSelectedProtocolIds([]);
        return;
      }

      try {
        setLoadingProtocols(true);
        const result = await getAnalyticProtocols(selectedStudyId);

        if (!isMounted) {
          return;
        }

        const sortedProtocols = [...result].sort(
          (leftProtocol, rightProtocol) =>
            String(leftProtocol.reference || "").localeCompare(
              String(rightProtocol.reference || ""),
            ),
        );

        setProtocols(sortedProtocols);
        setSelectedProtocolIds((currentProtocolIds) =>
          currentProtocolIds.filter((protocolId) =>
            sortedProtocols.some(
              (protocol) => String(protocol.id) === String(protocolId),
            ),
          ),
        );
      } catch (loadError) {
        if (isMounted) {
          setProtocols([]);
          setSelectedProtocolIds([]);
          setError(t("analyticsProtocolLoadError"));
        }
      } finally {
        if (isMounted) {
          setLoadingProtocols(false);
        }
      }
    };

    loadProtocols();

    return () => {
      isMounted = false;
    };
  }, [selectedStudyId, t]);

  const selectedStudy = React.useMemo(
    () =>
      studies.find((study) => String(study.id) === String(selectedStudyId)) ||
      null,
    [selectedStudyId, studies],
  );

  const resultColumns = React.useMemo(
    () => [
      {
        field: "materialName",
        headerName: t("materialType"),
        flex: 1,
        minWidth: 220,
      },
      {
        field: "productionId",
        headerName: t("productionId"),
        flex: 0.4,
        minWidth: 110,
      },
      {
        field: "materialId",
        headerName: t("materialId"),
        flex: 0.4,
        minWidth: 110,
      },
      ...(includeProtocolColumn
        ? [
            {
              field: "protocolReference",
              headerName: t("protocols"),
              flex: 0.8,
              minWidth: 180,
            },
          ]
        : []),
      {
        field: "productionDate",
        headerName: t("productionDate"),
        flex: 0.7,
        minWidth: 160,
      },
      {
        field: "qcDate",
        headerName: t("qcDate"),
        flex: 0.7,
        minWidth: 150,
      },
      {
        field: "count",
        headerName: t("count"),
        type: "number",
        flex: 0.4,
        minWidth: 120,
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {value ?? ""}
          </Typography>
        ),
      },
      {
        field: "unitPrice",
        headerName: t("unitPrice"),
        type: "number",
        flex: 0.45,
        minWidth: 130,
        valueFormatter: ({ value }) => formatPrice(value),
      },
      {
        field: "subPrice",
        headerName: t("subPrice"),
        type: "number",
        flex: 0.45,
        minWidth: 130,
        valueFormatter: ({ value }) => formatPrice(value),
      },
    ],
    [includeProtocolColumn, t],
  );

  const detailColumns = React.useMemo(
    () => [
      {
        field: "materialName",
        headerName: t("materialType"),
        flex: 1,
        minWidth: 220,
      },
      {
        field: "productionId",
        headerName: t("productionId"),
        flex: 0.4,
        minWidth: 110,
      },
      {
        field: "materialId",
        headerName: t("materialId"),
        flex: 0.4,
        minWidth: 110,
      },
      ...(includeProtocolColumn
        ? [
            {
              field: "protocolReference",
              headerName: t("protocols"),
              flex: 0.8,
              minWidth: 180,
            },
          ]
        : []),
      {
        field: "productionDate",
        headerName: t("productionDate"),
        flex: 0.7,
        minWidth: 160,
      },
      {
        field: "qcDate",
        headerName: t("qcDate"),
        flex: 0.7,
        minWidth: 150,
      },
    ],
    [includeProtocolColumn, t],
  );

  const selectedProtocolLabel = React.useMemo(() => {
    if (protocolMode === "different") {
      return t("different");
    }

    if (protocolMode === "all" || selectedProtocolIds.length === 0) {
      return t("all");
    }

    return protocols
      .filter(
        (protocol) =>
          selectedProtocolIds.includes(String(protocol.id)) ||
          selectedProtocolIds.includes(protocol.id),
      )
      .map((protocol) => protocol.reference)
      .join(", ");
  }, [protocolMode, protocols, selectedProtocolIds, t]);

  const protocolSelectValue = React.useMemo(() => {
    if (protocolMode === "different") {
      return ["different"];
    }

    if (protocolMode === "all") {
      return ["all"];
    }

    return selectedProtocolIds;
  }, [protocolMode, selectedProtocolIds]);

  const handleSubmit = async () => {
    if (!selectedStudyId) {
      setError(t("analyticsStudyRequired"));
      return;
    }

    try {
      setError("");
      setLoadingResults(true);
      const result = await runAnalyticQuery({
        studyId: Number(selectedStudyId),
        protocolMode,
        protocolIds: selectedProtocolIds.map((protocolId) =>
          Number(protocolId),
        ),
        from: from || null,
        to: to || null,
      });

      const nextIncludeProtocolColumn = Boolean(
        result.includeProtocolColumn ?? result.groupByProtocol,
      );
      const transformedRows = [...(result.rows || [])]
        .map((row) => ({
          ...row,
          materialName: getTransformedMaterialName(row.materialName),
        }))
        .sort(sortAnalyticRows);
      const transformedDetailRows = [...(result.detailRows || [])]
        .map((row) => ({
          ...row,
          materialName: getTransformedMaterialName(row.materialName),
        }))
        .sort(sortAnalyticRows);

      setRows(transformedRows);
      setDetailRows(transformedDetailRows);
      setExportRows(
        buildExportRows(transformedRows, nextIncludeProtocolColumn),
      );
      setIncludeProtocolColumn(nextIncludeProtocolColumn);
      setHasSubmitted(true);
    } catch (requestError) {
      setRows([]);
      setDetailRows([]);
      setExportRows([]);
      setIncludeProtocolColumn(false);
      setHasSubmitted(true);
      setError(
        requestError?.response?.data?.message || t("analyticsQueryError"),
      );
    } finally {
      setLoadingResults(false);
    }
  };

  const exportFilename = React.useMemo(() => {
    const studyCode = getStudyLabel(selectedStudy);
    return `${studyCode || "analytics"}_${getDateRangeLabel(from, to)}`;
  }, [from, selectedStudy, to]);

  const totalProductions = React.useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.count || 0), 0),
    [rows],
  );
  const totalSubPrice = React.useMemo(
    () =>
      rows.reduce((sum, row) => {
        const numericSubPrice = Number(row.subPrice);
        return Number.isNaN(numericSubPrice) ? sum : sum + numericSubPrice;
      }, 0),
    [rows],
  );

  const resultsSectionHeight = "70vh";
  const detailsSectionHeight = "95vh";

  return (
    <Grid item xs={12}>
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            border: (theme) =>
              `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
          }}>
          <Box
            sx={{
              px: 3,
              py: 2.5,
              background: (theme) =>
                `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.14,
                )}, ${alpha(theme.palette.secondary.main, 0.08)})`,
              borderBottom: (theme) =>
                `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between">
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <InsightsOutlinedIcon color="primary" />
                  <Typography variant="h5">{t("analytics")}</Typography>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}>
                  {t("analyticsDescription")}
                </Typography>
              </Box>
              {exportRows.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={() =>
                    downloadExcel(exportRows, exportFilename, {
                      dateRange: getExportDateRangeText(from, to),
                      total: formatTotalPrice(totalSubPrice),
                    })
                  }>
                  {t("exportExcel")}
                </Button>
              )}
            </Stack>
          </Box>

          <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
              {error && <Alert severity="error">{error}</Alert>}

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                }}>
                <FormControl fullWidth required disabled={loadingStudies}>
                  <InputLabel id="analytic-study-label">
                    {t("study")}
                  </InputLabel>
                  <Select
                    labelId="analytic-study-label"
                    label={t("study")}
                    value={selectedStudyId}
                    onChange={(event) => {
                      setError("");
                      setProtocolMode("all");
                      setSelectedStudyId(event.target.value);
                      setSelectedProtocolIds([]);
                      setRows([]);
                      setDetailRows([]);
                      setExportRows([]);
                      setIncludeProtocolColumn(false);
                      setHasSubmitted(false);
                    }}>
                    {studies.map((study) => (
                      <MenuItem key={study.id} value={String(study.id)}>
                        {getStudyLabel(study)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {loadingStudies
                      ? t("loading")
                      : t("analyticsStudyRequiredHint")}
                  </FormHelperText>
                </FormControl>

                <FormControl
                  fullWidth
                  disabled={!selectedStudyId || loadingProtocols}
                  sx={{ minWidth: 0 }}>
                  <InputLabel id="analytic-protocols-label">
                    {t("protocols")}
                  </InputLabel>
                  <Select
                    multiple
                    labelId="analytic-protocols-label"
                    label={t("protocols")}
                    value={protocolSelectValue}
                    onChange={(event) => {
                      const value = event.target.value;
                      const nextValues =
                        typeof value === "string" ? value.split(",") : value;
                      const explicitProtocolIds = nextValues.filter(
                        (entry) => entry !== "all" && entry !== "different",
                      );

                      setError("");
                      if (explicitProtocolIds.length > 0) {
                        setProtocolMode("selected");
                        setSelectedProtocolIds(explicitProtocolIds);
                      } else if (nextValues.includes("different")) {
                        setProtocolMode("different");
                        setSelectedProtocolIds([]);
                      } else if (nextValues.includes("all")) {
                        setProtocolMode("all");
                        setSelectedProtocolIds([]);
                      } else {
                        setProtocolMode("all");
                        setSelectedProtocolIds([]);
                      }
                      setRows([]);
                      setDetailRows([]);
                      setExportRows([]);
                      setIncludeProtocolColumn(false);
                      setHasSubmitted(false);
                    }}
                    renderValue={() => selectedProtocolLabel}>
                    <MenuItem value="all">{t("all")}</MenuItem>
                    <MenuItem value="different">{t("different")}</MenuItem>
                    {protocols.map((protocol) => (
                      <MenuItem key={protocol.id} value={String(protocol.id)}>
                        {protocol.reference}
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingProtocols && (
                    <FormHelperText>{t("loading")}</FormHelperText>
                  )}
                </FormControl>

                <TextField
                  fullWidth
                  label={t("from")}
                  type="date"
                  value={from}
                  onChange={(event) => {
                    setError("");
                    setFrom(event.target.value);
                    setRows([]);
                    setDetailRows([]);
                    setExportRows([]);
                    setIncludeProtocolColumn(false);
                    setHasSubmitted(false);
                  }}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  fullWidth
                  label={t("to")}
                  type="date"
                  value={to}
                  onChange={(event) => {
                    setError("");
                    setTo(event.target.value);
                    setRows([]);
                    setDetailRows([]);
                    setExportRows([]);
                    setIncludeProtocolColumn(false);
                    setHasSubmitted(false);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between">
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {selectedStudy && (
                    <Chip
                      color="primary"
                      variant="outlined"
                      label={`${t("study")}: ${getStudyLabel(selectedStudy)}`}
                    />
                  )}
                  <Chip
                    variant="outlined"
                    label={`${t("protocols")}: ${selectedProtocolLabel}`}
                  />
                </Stack>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  disabled={!selectedStudyId || loadingResults}
                  onClick={handleSubmit}>
                  {t("runAnalytics")}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 0,
            border: (theme) =>
              `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              borderBottom: (theme) =>
                `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}>
            <Typography variant="h6">{t("analyticsResults")}</Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {`${rows.length} / ${totalProductions}`}
              </Typography>
              <Button
                size="small"
                variant="text"
                startIcon={
                  resultsOpen ? (
                    <ExpandLessOutlinedIcon />
                  ) : (
                    <ExpandMoreOutlinedIcon />
                  )
                }
                onClick={() => setResultsOpen((current) => !current)}>
                {resultsOpen ? t("analyticsClose") : t("analyticsShow")}
              </Button>
            </Stack>
          </Box>

          {resultsOpen && !loadingResults && !hasSubmitted && (
            <Box sx={{ px: 3, pt: 3 }}>
              <Alert severity="info">{t("analyticsEmptyState")}</Alert>
            </Box>
          )}

          {resultsOpen && (
            <Box>
              <Box sx={{ width: "100%", height: resultsSectionHeight }}>
                {loadingResults ? (
                  <LinearProgress />
                ) : (
                  <LocalizedDataGrid
                    disableRowSelectionOnClick
                    rows={rows}
                    columns={resultColumns}
                    slots={{
                      noRowsOverlay: NoRowOverlay,
                    }}
                    hideFooter={rows.length <= 25}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 25 },
                      },
                    }}
                    pageSizeOptions={[25, 50, 100]}
                    rowHeight={38}
                    sx={{
                      border: 0,
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: (theme) =>
                          alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  />
                )}
              </Box>

              {!loadingResults && hasSubmitted && (
                <Box
                  sx={{
                    px: 3,
                    py: 1.5,
                    display: "flex",
                    justifyContent: "flex-end",
                    borderTop: (theme) =>
                      `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.03),
                  }}>
                  <Typography variant="h6" sx={{ fontWeight: 400 }}>
                    {`${t("total")}: ${formatTotalPrice(totalSubPrice)}`}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 0,
            border: (theme) =>
              `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              borderBottom: (theme) =>
                `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}>
            <Typography variant="h6">{t("analyticsDetails")}</Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {detailRows.length}
              </Typography>
              <Button
                size="small"
                variant="text"
                startIcon={
                  detailsOpen ? (
                    <ExpandLessOutlinedIcon />
                  ) : (
                    <ExpandMoreOutlinedIcon />
                  )
                }
                onClick={() => setDetailsOpen((current) => !current)}>
                {detailsOpen ? t("analyticsClose") : t("analyticsShow")}
              </Button>
            </Stack>
          </Box>

          {detailsOpen && (
            <Box sx={{ width: "100%", height: detailsSectionHeight }}>
              {loadingResults ? (
                <LinearProgress />
              ) : (
                <LocalizedDataGrid
                  disableRowSelectionOnClick
                  rows={detailRows}
                  columns={detailColumns}
                  slots={{
                    noRowsOverlay: NoRowOverlay,
                  }}
                  hideFooter={detailRows.length <= 25}
                  initialState={{
                    pagination: {
                      paginationModel: { page: 0, pageSize: 25 },
                    },
                  }}
                  pageSizeOptions={[25, 50, 100]}
                  rowHeight={38}
                  sx={{
                    border: 0,
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                />
              )}
            </Box>
          )}
        </Paper>
      </Stack>
    </Grid>
  );
};
